<template lang="pug">
  .rc-autocomplete(v-click-outside="clickOutside")
    .search-input
      .selected-chips(v-if="multiple && selected.length && allItems")
        .selected-chip(
          v-for="value in selected"
          :key="value"
        )
          span.text {{ findOption(value)[itemText] }}
          i.material-icons(@click="selectItem(value, false)") close
        .divider
      .search-text-field
        input(
          ref="searchInput"
          type="text"
          v-model="searchValue"
          :placeholder="placeholder"
          @focus="inputFocusHandler"
          @focusout="inputFocused = multiple"
          @input="searchInputHandler"
        )
        i.material-icons search
    .linear-progress.small(v-if="loading")
      .bar.bar1
      .bar.bar2
    .options(v-show="inputFocused" ref="options")
      .option(
        v-for="(item, idx) in items"
        :key="`option-${idx}`"
        @mousedown="selectItem(item, multiple ? !selected.includes(item[itemValue]) : true)"
      )
        slot(name="option" :item="item")
          span {{ item[itemText] }}
        label.checkbox-wrapper(v-if="multiple")
          input(type="checkbox" :checked="selected.includes(item[itemValue])")
          .checkbox-indicator
      .option(v-if="!loading && items && !items.length && searchValue.length")
        span Aucun résultat
</template>

<script>
import rcDebounce from '@/mixins/rcDebounce.js';
import { clickOutside } from '@/directives/click-outside/clickOutside.js';

export default {
  mixins: [rcDebounce],
  directives: {clickOutside},
  props: {
    itemText: {
      type: String,
      default: 'label',
    },
    itemValue: {
      type: String,
      default: 'id',
    },
    searchFunction: {
      type: Function,
      default: () => [],
    },
    debounce: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputFocused: false,
      SEARCH_DEBOUNCE: 1000,
      allItems: null,
      items: [],
      selected: this.multiple ? [] : null,
      searchValue: '',
      loading: false,
    };
  },
  methods: {
    clickOutside(e) {
      this.inputFocused = false;
    },
    async search(search) {
      if (!this.debounce || (search && search.length)) {
        this.items = await this.searchFunction(search);
      } else {
        this.items = [];
      }
      this.loading = false;
    },
    emitSelect() {
      this.$emit('select', this.selected);
    },
    reset() {
      this.selected = null;
      this.searchValue = '';
    },
    searchInputHandler(e) {
      if (e.target.value.length) {
        this.loading = true;
      }
      if (!this.multiple) {
        this.selected = null;
        this.emitSelect();
      }
      if (this.debounce) {
        this.rcDebounce(() => { this.search(e.target.value) }, this.SEARCH_DEBOUNCE);
      } else {
        this.search(e.target.value);
      }
    },
    selectItem(item, selected) {
      if (this.multiple) {
        const value = typeof item === 'object' ? item[this.itemValue] : item;
        this.selected = this.toggleInArray(this.selected, value, selected);
      } else {
        this.selected = item;
        this.searchValue = this.itemText ? item[this.itemText] : item;
      }
      this.emitSelect();
    },
    toggleInArray(array, element, checked) {
      return checked ? [...array, element] : array.filter(it => it !== element);
    },
    async inputFocusHandler() {
      if (!this.searchValue.length && !this.debounce && !this.allItems) {
        this.items = this.searchFunction('');
        this.allItems = this.items;
      }
      this.inputFocused = true;
      await this.$nextTick();
      const element = this.$refs.searchInput;
      const options = this.$refs.options;
      const domRect = element.getBoundingClientRect();
      const bottomOffset = window.innerHeight - domRect.bottom;
      options.style.maxHeight = `${bottomOffset > 500 ? 500 : bottomOffset}px`;
    },
    findOption(value) {
      return this.allItems.find(it => it[this.itemValue] === value);
    },
  },
}
</script>

<style lang="scss">
  @import './../styles/linear-progress.css';
  .rc-autocomplete {
    .search-input {
      background: white;
      border-bottom: 1px solid #BABABA;
      width: 320px;
      display: flex;
      flex-direction: column;
      color: #BABABA;
      .search-text-field {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }
      input {
        &::placeholder {
          color: #BABABA;
        }
        outline: none;
        font-size: 16px;
        border: none;
        width: 100%;
        padding: 4px;
      }
      .selected-chips {
        display: flex;
        flex-wrap: wrap;
        .selected-chip {
          background: var(--primary);
          color: white;
          display: flex;
          align-items: center;
          border-radius: .75em;
          padding: 4px .5em;
          margin: 4px;
          font-size: 12px;
          i {
            cursor: pointer;
            font-size: 14px;
            margin-left: 4px;
            margin-right: -2px;
            background: rgba(255, 255, 255, 0.8);
            color: #3C3C3C;
            border-radius: 50%;
          }
        }
        .divider {
          height: 1px;
          width: 100%;
          margin: 2px 4px;
          background: #DDDDDD;
        }
      }
    }
    .options {
      position: absolute;
      background: white;
      box-shadow: 2px 2px 4px #666;
      overflow-y: auto;
      min-width: 320px;
      z-index: 2;
      .option {
        display: flex;
        justify-content: space-between;
        padding: 12px 20px;
        border-bottom: 1px solid #CACACA;
        cursor: pointer;
        &:hover {
          background: #EAEAEA;
        }
        .checkbox-wrapper {
          top: 6px;
        }
      }
    }
    .linear-progress {
      width: 320px;
    }
  }
</style>